import React, { useState } from 'react';
import {
  Box, Typography, Paper, Chip, Tabs, Tab, Divider, List, ListItem, ListItemIcon, ListItemText, IconButton, Tooltip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  SmartToy as AIIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Transform as TransformIcon,
  CallSplit as ConditionalIcon,
  Loop as LoopIcon,
  Webhook as WebhookIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Message as MessageIcon,
  TextFields as TextIcon,
  Storage as DatabaseIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationIcon,
  CloudUpload as FileIcon,
  Psychology as DecisionIcon,
  Extension as PluginIcon,
  Timeline as EdgeIcon,
  DataObject as StateIcon,
  Build as ToolIcon,
  Cable as ConnectorIcon,
  ArrowForward as ArrowIcon,
  QuestionMark as ConditionIcon,
  Brightness1 as DotIcon,
  Settings as SettingsIcon,
  Memory as MemoryIcon,
  Functions as FunctionIcon,
  Router as RouterIcon,
  Event as EventIcon,
  Http as HttpIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Group as GroupIcon,
  MergeType as MergeIcon,
  Repeat as RepeatIcon,
  ToggleOn as SwitchIcon,
  BugReport as TryIcon,
  Pause as PauseIcon,
  TouchApp as TriggerIcon,
  KeyboardReturn as ReturnIcon,
  ExitToApp as ExitIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  Star as StarIcon,
  BookmarkBorder as BookmarkIcon,
  Folder as FolderIcon,
  Label as TagIcon,
  Description as DescriptionIcon,
  Policy as PolicyIcon,
  Title as TitleIcon,
  Subject as SubjectIcon,
  Notes as NotesIcon,
  Comment as CommentIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  QuestionAnswer as QAIcon,
  ContactSupport as SupportIcon,
  DeviceHub as HubConnectorIcon,
  PowerInput as PowerIcon,
  Power as ElectricalIcon,
  TouchApp as TouchIcon,
  ToggleOn as ToggleIcon,
  List as ListIcon,
  Speed as SpeedIcon,
  BugReport as BugIcon,
  CheckCircle as CheckIcon,
  GetApp as GetAppIcon,
  Publish as PublishIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Verified as VerifiedIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Call as CallIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

function ComprehensivePalette({
  onAddNode, onAddEdge,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Define node sections first
  const nodeSections = [
    {
      title: 'AI & Logic',
      items: [
        {
          type: 'ai-agent', label: 'AI Agent', icon: <AIIcon />, color: '#4CAF50', description: 'Intelligent agent with LLM capabilities',
        },
        {
          type: 'code-executor', label: 'Code Executor', icon: <CodeIcon />, color: '#2196F3', description: 'Execute custom code logic',
        },
        {
          type: 'decision-tree', label: 'Decision Tree', icon: <DecisionIcon />, color: '#FF9800', description: 'Complex decision branching',
        },
        {
          type: 'conditional', label: 'Conditional', icon: <ConditionalIcon />, color: '#F44336', description: 'If-then-else logic',
        },
        {
          type: 'loop', label: 'Loop', icon: <LoopIcon />, color: '#607D8B', description: 'Iterate over data',
        },
        {
          type: 'switch', label: 'Switch', icon: <SwitchIcon />, color: '#9C27B0', description: 'Multi-branch logic',
        },
        {
          type: 'try-catch', label: 'Try-Catch', icon: <TryIcon />, color: '#E91E63', description: 'Error handling',
        },
      ],
    },
    {
      title: 'Data & API',
      items: [
        {
          type: 'rag', label: 'RAG Retriever', icon: <MemoryIcon />, color: '#673ab7', description: 'Retrieval Augmented Generation node',
        },
        {
          type: 'policy', label: 'Policy Node', icon: <PolicyIcon />, color: '#2e7d32', description: 'Process policy docs into Q&A-ready model for interview questions',
        },
        {
          type: 'api-call', label: 'API Call', icon: <ApiIcon />, color: '#FF9800', description: 'HTTP API requests',
        },
        {
          type: 'webhook', label: 'Webhook', icon: <WebhookIcon />, color: '#3F51B5', description: 'Receive HTTP callbacks',
        },
        {
          type: 'web-crawler', label: 'Web Crawler', icon: <SearchIcon />, color: '#795548', description: 'Fetch and extract web page content',
        },
        {
          type: 'connector', label: 'Connector', icon: <HubConnectorIcon />, color: '#9C27B0', description: 'Integrate with Slack, HubSpot, HTTP, and more',
        },
        {
          type: 'database', label: 'Database', icon: <DatabaseIcon />, color: '#795548', description: 'Database operations',
        },
        // --- Enhanced Form Node ---
        {
          type: 'form',
          label: 'Form',
          icon: <SubjectIcon />,
          color: '#1976d2',
          description: 'Human-in-the-loop form: collect data and submit to DB as part of workflow',
        },
      ],
    },
    {
      title: 'Legal (Clio)',
      items: [
        {
          type: 'clio-conflict-check',
          label: 'Clio Conflict Check',
          icon: <SearchIcon />,
          color: '#1976d2',
          description: 'Check for conflicts of interest in Clio database',
        },
        {
          type: 'clio-create-matter',
          label: 'Clio Create Matter',
          icon: <AddIcon />,
          color: '#1976d2',
          description: 'Create new matter in Clio practice management',
        },
        {
          type: 'clio-post-time',
          label: 'Clio Post Time',
          icon: <EventIcon />,
          color: '#1976d2',
          description: 'Post billable time entry to Clio matter',
        },
        {
          type: 'clio-upload-document',
          label: 'Clio Upload Document',
          icon: <CloudUploadIcon />,
          color: '#1976d2',
          description: 'Upload document to Clio matter files',
        },
      ],
    },
    {
      title: 'Communication',
      items: [
        {
          type: 'email', label: 'Email', icon: <EmailIcon />, color: '#F44336', description: 'Send email messages',
        },
        {
          type: 'sms', label: 'SMS', icon: <SmsIcon />, color: '#4CAF50', description: 'Send SMS messages',
        },
        {
          type: 'chat', label: 'Chat', icon: <ChatIcon />, color: '#2196F3', description: 'Chat interface',
        },
        {
          type: 'human-input', label: 'Human Input', icon: <PersonIcon />, color: '#FF9800', description: 'Get user input',
        },
        {
          type: 'calendar', label: 'Calendar', icon: <EventIcon />, color: '#E91E63', description: 'Calendar operations',
        },
        {
          type: 'notification', label: 'Notification', icon: <NotificationIcon />, color: '#FF9800', description: 'Push notifications',
        },
        {
          type: 'message-queue', label: 'Message Queue', icon: <MessageIcon />, color: '#9C27B0', description: 'Queue messages',
        },
        {
          type: 'voice-call', label: 'Voice Call', icon: <CallIcon />, color: '#E91E63', description: 'Voice communication',
        },
        {
          type: 'social-post',
          label: 'Social Post/Ad',
          icon: <PublishIcon />,
          color: '#1976d2',
          description: 'Create and publish social media posts/ads',
        },
      ],
    },
    {
      title: 'AI & Memory',
      items: [
        {
          type: 'memory', label: 'Memory', icon: <MemoryIcon />, color: '#9C27B0', description: 'Memory operations (store/retrieve/clear)',
        },
        {
          type: 'tool', label: 'Tool', icon: <ToolIcon />, color: '#FF9800', description: 'Execute tools and functions',
        },
      ],
    },
    {
      title: 'Control Flow',
      items: [
        {
          type: 'trigger', label: 'Trigger', icon: <TriggerIcon />, color: '#FF9800', description: 'Event trigger',
        },
        {
          type: 'scheduler', label: 'Scheduler', icon: <ScheduleIcon />, color: '#3F51B5', description: 'Time-based triggers',
        },
        {
          type: 'delay', label: 'Delay', icon: <PauseIcon />, color: '#607D8B', description: 'Wait/pause execution',
        },
        {
          type: 'parallel', label: 'Parallel', icon: <ConditionalIcon />, color: '#9C27B0', description: 'Parallel execution',
        },
        {
          type: 'join', label: 'Join', icon: <MergeIcon />, color: '#795548', description: 'Wait for parallel branches',
        },
        {
          type: 'router', label: 'Router', icon: <RouterIcon />, color: '#009688', description: 'Route to different paths',
        },
        {
          type: 'execute-flow', label: 'Execute Flow', icon: <HubConnectorIcon />, color: '#1976d2', description: 'Run another flow as subflow and use its result',
        },
      ],
    },
    {
      title: 'Development Tools',
      items: [
        {
          type: 'logger', label: 'Logger', icon: <NotesIcon />, color: '#FF9800', description: 'Log flow execution data',
        },
        {
          type: 'debugger', label: 'Debugger', icon: <BugIcon />, color: '#F44336', description: 'Debug flow execution step-by-step',
        },
      ],
    },
    {
      title: 'External Tools',
      items: [
        {
          type: 'mcp-tool',
          label: 'MCP Tool',
          icon: <PluginIcon />,
          color: '#9C27B0',
          description: 'Connect to external MCP servers and execute tools',
          toolName: '',
          availableTools: [
            'database_query',
            'file_operations',
            'ai_processor',
            'http_request',
          ],
        },
      ],
    },
  ];

  // Filter nodes based on search query and category
  const filteredSections = nodeSections.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      const matchesSearch = searchQuery === ''
        || item.label.toLowerCase().includes(searchQuery.toLowerCase())
        || item.description.toLowerCase().includes(searchQuery.toLowerCase())
        || item.type.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = categoryFilter === 'all'
        || section.title.toLowerCase().includes(categoryFilter.toLowerCase());

      return matchesSearch && matchesCategory;
    }),
  })).filter((section) => section.items.length > 0);

  // Get all categories for filter dropdown
  const categories = ['all', ...nodeSections.map((section) => section.title)];

  // Debug logging (commented out to reduce console clutter)
  // console.log('🎨 ComprehensivePalette rendering with', nodeSections.length, 'sections');

  const handleItemClick = (item) => {
    // Click-to-add is disabled - users must use drag and drop
    // This provides better control over node placement
    // console.log('🎯 handleItemClick called with item:', item);
    // console.log('🎯 Item type:', item.type);
    // console.log('🎯 Click-to-add disabled - use drag and drop instead');
  };

  const handleDragStart = (e, item) => {
    // Create a clean object without circular references
    const cleanItem = {
      type: item.type,
      label: item.label,
      color: item.color,
      description: item.description,
      ...(item.toolName && { toolName: item.toolName }),
      ...(item.serverUrl && { serverUrl: item.serverUrl }),
      ...(item.authentication && { authentication: item.authentication }),
      ...(item.timeout && { timeout: item.timeout }),
      ...(item.retries && { retries: item.retries }),
    };

    const itemJson = JSON.stringify(cleanItem);

    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';

    try {
      // Set data in multiple formats to ensure compatibility
      e.dataTransfer.setData('application/json', itemJson);
      e.dataTransfer.setData('text/plain', itemJson);
      e.dataTransfer.setData('text/html', itemJson);
      e.dataTransfer.effectAllowed = 'copy';
    } catch (error) {
      console.error('Error setting data transfer:', error);
    }
  };

  const handleDragEnd = (e) => {
    // Remove visual feedback
    e.currentTarget.style.opacity = '1';
  };

  if (collapsed) {
    return (
      <Box sx={{
        width: 40, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider', pt: 1,
      }}
      >
        <Tooltip title="Expand Palette">
          <IconButton onClick={() => setCollapsed(false)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper sx={{
      width: 320,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
    }}
    >
      {/* Collapse Button */}
      <IconButton
        size="small"
        onClick={() => setCollapsed(true)}
        sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 10,
        }}
        title="Collapse Palette"
      >
        <ChevronLeftIcon />
      </IconButton>

      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
          Flow Builder Palette
        </Typography>

        {/* Search and Filter Controls */}
        <Stack spacing={1}>
          {/* Search Bar */}
          <TextField
            size="small"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              },
            }}
          />

          {/* Filter Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.slice(1).map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{ color: 'white' }}
              title="Toggle Advanced Filters"
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Search Results Count */}
          {searchQuery && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {filteredSections.reduce((total, section) => total + section.items.length, 0)}
              {' '}
              nodes found
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Items List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filteredSections.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No nodes found matching your search
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Try adjusting your search terms or filters
            </Typography>
          </Box>
        ) : (
          filteredSections.map((section, sectionIndex) =>
          // console.log('🎨 Rendering section:', section.title, 'with items:', section.items?.length);
          // section.items?.forEach(item => {
          //   console.log('🎨 Item in section:', item.type, item.label);
          // });
            (
              <Box key={sectionIndex} sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                  }}
                >
                  {section.title}
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {section.items?.map((item, itemIndex) => (
                    <Tooltip
                      key={itemIndex}
                      title={
                    item.mode
                      ? `Switch to ${item.label} mode`
                      : item.description || `Add ${item.label}`
                  }
                      placement="right"
                    >
                      <ListItem
                        onClick={(e) => {
                          // Click disabled - use drag and drop instead
                        }}
                        sx={{
                          cursor: 'grab',
                          '&:active': {
                            cursor: 'grabbing',
                          },
                          ...(item.type === 'logger' && {
                            backgroundColor: '#FF980010',
                            border: '1px solid #FF980030',
                            '&:hover': {
                              backgroundColor: '#FF980020',
                              border: '1px solid #FF980050',
                            },
                          }),
                          ...(item.type === 'debugger' && {
                            backgroundColor: '#F4433610',
                            border: '1px solid #F4433630',
                            '&:hover': {
                              backgroundColor: '#F4433620',
                              border: '1px solid #F4433650',
                            },
                          }),
                          borderRadius: 1,
                          mb: 0.5,
                          border: 1,
                          borderColor: `${item.color}40`,
                          backgroundColor: `${item.color}10`,
                          '&:hover': {
                            backgroundColor: `${item.color}20`,
                            borderColor: item.color,
                          },
                          py: 0.5,
                          minHeight: 36,
                          // Visual styling for items
                        }}
                      >
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, item)}
                          onDragEnd={handleDragEnd}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'grab',
                            userSelect: 'none', // Prevent text selection during drag
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: item.color }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            secondary={item.description}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.7rem',
                              noWrap: true,
                            }}
                          />
                        </div>
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
                {sectionIndex < nodeSections.length - 1 && (
                <Divider sx={{ my: 1 }} />
                )}
              </Box>
            ))
        )}
      </Box>

      {/* Help Text */}
      <Box sx={{
        p: 2, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider',
      }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 500 }}>
          Drag nodes onto the canvas to add them
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
          • Ctrl/Cmd + click to connect nodes
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
          • Double-click to configure
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
          • Right-click for more options
        </Typography>
      </Box>
    </Paper>
  );
}

export default ComprehensivePalette;
